'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

interface IndustrySolution {
  id: string;
  name: string;
  description: string;
  icon: string;
  image: string;
  alt: string;
  carbonReduction: string;
  creditPotential: string;
  solutions: string[];
  technologies: string[];
  outcomes: string[];
}

const IndustryESGInteractive = () => {
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');

  const industries: IndustrySolution[] = [
  {
    id: 'oil-gas',
    name: 'Oil & Gas',
    description: 'Comprehensive emission monitoring and reduction strategies for upstream, midstream, and downstream operations with advanced leak detection and process optimization.',
    icon: 'FireIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cfebf842-1764815748893.png",
    alt: 'Modern oil and gas refinery facility with emission monitoring systems at sunset',
    carbonReduction: '25-40% emission reduction',
    creditPotential: 'High - Methane capture & flaring optimization',
    solutions: [
    'Upstream emission monitoring',
    'Midstream pipeline optimization',
    'Downstream refinery efficiency',
    'Methane leak detection (AI/IoT)',
    'Flaring optimization systems',
    'Carbon capture integration'],

    technologies: [
    'Satellite methane monitoring',
    'IoT sensor networks',
    'AI predictive maintenance',
    'Digital twin modeling'],

    outcomes: [
    'Regulatory compliance (OGMP 2.0)',
    'Reduced methane emissions',
    'Operational efficiency gains',
    'Carbon credit generation']

  },
  {
    id: 'hydro-power',
    name: 'Hydro Power',
    description: 'Sustainable hydroelectric operations with reservoir carbon assessment, turbine efficiency optimization, and ecosystem impact monitoring.',
    icon: 'BoltIcon',
    image: "https://images.unsplash.com/photo-1729312899738-0144c1820770",
    alt: 'Large hydroelectric dam with turbines generating clean renewable energy surrounded by mountains',
    carbonReduction: '15-30% carbon intensity reduction',
    creditPotential: 'Medium - Clean energy generation credits',
    solutions: [
    'Reservoir carbon assessment',
    'Turbine efficiency optimization',
    'Ecosystem impact monitoring',
    'Sediment management',
    'Fish passage systems',
    'Methane emission control'],

    technologies: [
    'Underwater carbon sensors',
    'Turbine performance analytics',
    'Biodiversity monitoring AI',
    'Sediment flow modeling'],

    outcomes: [
    'Reduced reservoir emissions',
    'Enhanced energy output',
    'Ecosystem preservation',
    'International carbon credits']

  },
  {
    id: 'banking',
    name: 'Banking & Finance',
    description: 'Financial sector sustainability tools for financed emissions tracking, green lending portfolio management, and climate risk assessment.',
    icon: 'BanknotesIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf1b2d61-1764715714064.png",
    alt: 'Modern banking office with sustainable finance dashboard showing green investment metrics',
    carbonReduction: 'Portfolio-level impact tracking',
    creditPotential: 'Low - Focus on financed emissions',
    solutions: [
    'Financed emissions tracking',
    'Green lending portfolio management',
    'Climate risk assessment frameworks',
    'TCFD alignment support',
    'FIBAC reporting integration',
    'ESG investment screening'],

    technologies: [
    'Carbon accounting platforms',
    'Climate risk modeling',
    'Portfolio analytics AI',
    'Regulatory reporting automation'],

    outcomes: [
    'TCFD/FIBAC compliance',
    'Reduced financed emissions',
    'Enhanced ESG ratings',
    'Competitive green finance edge']

  },
  {
    id: 'data-centers',
    name: 'Data Centers',
    description: 'Energy efficiency optimization for digital infrastructure with renewable energy integration and advanced cooling system strategies.',
    icon: 'ServerIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1730627d9-1764648225985.png",
    alt: 'State-of-the-art data center with efficient cooling systems and renewable energy powered servers',
    carbonReduction: '30-50% energy consumption reduction',
    creditPotential: 'High - Renewable energy & efficiency',
    solutions: [
    'Energy efficiency optimization',
    'Renewable energy integration',
    'Cooling system carbon reduction',
    'Server virtualization',
    'Waste heat recovery',
    'AI-driven load management'],

    technologies: [
    'Smart cooling systems',
    'AI workload optimization',
    'Renewable energy management',
    'Heat recovery systems'],

    outcomes: [
    'Lower PUE (Power Usage Effectiveness)',
    'Reduced operational costs',
    'Green certification',
    'Carbon-neutral operations']

  },
  {
    id: 'mining',
    name: 'Mining',
    description: 'Sustainable extraction and processing with land rehabilitation planning, mineral processing efficiency, and environmental impact minimization.',
    icon: 'CubeIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1071e5c51-1764732294127.png",
    alt: 'Modern mining operation with land rehabilitation and environmental monitoring systems',
    carbonReduction: '20-35% operational emissions reduction',
    creditPotential: 'High - Land restoration & efficiency',
    solutions: [
    'Extraction impact minimization',
    'Land rehabilitation planning',
    'Mineral processing efficiency',
    'Water recycling systems',
    'Electric fleet transition',
    'Biodiversity offsetting'],

    technologies: [
    'Electric mining vehicles',
    'Process optimization AI',
    'Drone-based monitoring',
    'Water treatment systems'],

    outcomes: [
    'Reduced environmental footprint',
    'Land restoration credits',
    'Regulatory compliance',
    'Community acceptance']

  },
  {
    id: 'infrastructure',
    name: 'Infrastructure Development',
    description: 'Sustainable construction with lifecycle carbon analysis, green building certification pathways, and sustainable materials assessment.',
    icon: 'BuildingOffice2Icon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_166886890-1764712299471.png",
    alt: 'Green infrastructure construction site with sustainable building materials and solar panels',
    carbonReduction: '25-45% embodied carbon reduction',
    creditPotential: 'Medium - Green building & materials',
    solutions: [
    'Sustainable construction materials',
    'Lifecycle carbon analysis',
    'Green building certification',
    'Circular economy integration',
    'Low-carbon concrete',
    'Renewable energy integration'],

    technologies: [
    'BIM carbon modeling',
    'Material lifecycle databases',
    'Construction waste tracking',
    'Energy modeling software'],

    outcomes: [
    'LEED/GRIHA certification',
    'Lower embodied carbon',
    'Enhanced building value',
    'Operational efficiency']

  },
  {
    id: 'pharma',
    name: 'Pharmaceutical',
    description: 'Manufacturing process optimization with supply chain sustainability, waste reduction strategies, and regulatory compliance.',
    icon: 'BeakerIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cdf81921-1764654292439.png",
    alt: 'Modern pharmaceutical manufacturing facility with green chemistry and waste reduction systems',
    carbonReduction: '15-30% manufacturing emissions reduction',
    creditPotential: 'Medium - Process efficiency & waste',
    solutions: [
    'Manufacturing process optimization',
    'Supply chain sustainability',
    'Waste reduction strategies',
    'Cold chain efficiency',
    'Solvent recovery systems',
    'Water recycling'],

    technologies: [
    'Process analytics',
    'Green chemistry solutions',
    'Energy management systems',
    'Waste-to-energy systems'],

    outcomes: [
    'Lower production costs',
    'Regulatory compliance',
    'Enhanced brand reputation',
    'Operational resilience']

  },
  {
    id: 'plantations',
    name: 'Plantations',
    description: 'Sustainable agriculture with soil carbon enhancement, biodiversity conservation, and sustainable harvesting practices.',
    icon: 'SparklesIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13cd92816-1765534977339.png",
    alt: 'Sustainable plantation with diverse crops showing soil carbon enhancement and biodiversity',
    carbonReduction: 'Carbon sequestration & storage',
    creditPotential: 'Very High - Nature-based solutions',
    solutions: [
    'Soil carbon enhancement',
    'Biodiversity conservation',
    'Sustainable harvesting practices',
    'Agroforestry integration',
    'Organic farming transition',
    'Water conservation'],

    technologies: [
    'Soil carbon monitoring',
    'Precision agriculture',
    'Biodiversity sensors',
    'Remote sensing'],

    outcomes: [
    'Carbon credit generation',
    'Enhanced soil health',
    'Biodiversity protection',
    'Premium market access']

  },
  {
    id: 'biochar',
    name: 'Biochar Production',
    description: 'Carbon sequestration through optimized pyrolysis with feedstock selection, efficiency improvements, and verification systems.',
    icon: 'FireIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18b1eafd5-1764764836897.png",
    alt: 'Biochar production facility with sustainable pyrolysis process and carbon sequestration',
    carbonReduction: 'Net negative emissions',
    creditPotential: 'Very High - Carbon removal credits',
    solutions: [
    'Feedstock selection optimization',
    'Pyrolysis efficiency improvement',
    'Carbon sequestration verification',
    'Agricultural application',
    'Soil amendment strategies',
    'Carbon credit certification'],

    technologies: [
    'Advanced pyrolysis reactors',
    'Carbon stability analysis',
    'IoT process control',
    'MRV systems'],

    outcomes: [
    'Carbon removal credits',
    'Waste valorization',
    'Soil improvement',
    'Revenue generation']

  }];


  const filteredIndustries = selectedIndustry === 'all' ?
  industries :
  industries.filter((i) => i.id === selectedIndustry);

  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-secondary/10 via-surface to-primary/10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
              <Icon name="CubeIcon" size={20} className="text-accent" />
              <span className="text-sm font-semibold text-accent">Industry-Tailored Solutions</span>
            </div>
            
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-primary leading-tight">
              Industry-Specific ESG Solutions
            </h1>
            
            <p className="text-xl md:text-2xl text-text-secondary max-w-3xl mx-auto">
              Specialized strategies to lower carbon footprint and maximize carbon credit potential across diverse sectors
            </p>

            <div className="flex flex-wrap justify-center gap-4 pt-6">
              <Link
                href="/get-started"
                className="px-8 py-3 bg-conversion text-conversion-foreground font-heading font-semibold rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300">

                Get Started
              </Link>
              <a
                href="https://wa.me/919901503669"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-card text-text-primary font-heading font-semibold rounded-lg border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300">

                Contact Expert
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Filter */}
      <section className="py-8 bg-surface sticky top-16 z-40 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedIndustry('all')}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
              selectedIndustry === 'all' ? 'bg-primary text-primary-foreground shadow-natural' : 'bg-card text-text-secondary hover:bg-accent/10 border border-border'}`
              }>

              All Industries
            </button>
            {industries.map((industry) =>
            <button
              key={industry.id}
              onClick={() => setSelectedIndustry(industry.id)}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
              selectedIndustry === industry.id ?
              'bg-primary text-primary-foreground shadow-natural' :
              'bg-card text-text-secondary hover:bg-accent/10 border border-border'}`
              }>

                {industry.name}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {filteredIndustries.map((industry, index) =>
            <div
              key={industry.id}
              className={`bg-card rounded-2xl overflow-hidden border border-border hover:border-primary transition-all duration-300 hover:shadow-natural-lg ${
              index % 2 === 0 ? '' : ''}`
              }>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Image */}
                  <div className={`relative h-96 md:h-auto ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                    <AppImage
                    src={industry.image}
                    alt={industry.alt}
                    className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent"></div>
                  </div>

                  {/* Content */}
                  <div className={`p-8 ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name={industry.icon} size={28} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
                          {industry.name}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-3 py-1 bg-accent/10 text-accent text-xs font-semibold rounded-full">
                            {industry.carbonReduction}
                          </span>
                          <span className="px-3 py-1 bg-secondary/10 text-secondary text-xs font-semibold rounded-full">
                            {industry.creditPotential}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-text-secondary mb-6 leading-relaxed">
                      {industry.description}
                    </p>

                    <div className="space-y-4 mb-6">
                      <div>
                        <h4 className="font-semibold text-text-primary mb-3">Solutions:</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {industry.solutions.map((solution, idx) =>
                        <div key={idx} className="flex items-start space-x-2">
                              <Icon name="CheckCircleIcon" size={16} className="text-accent flex-shrink-0 mt-1" />
                              <span className="text-sm text-text-secondary">{solution}</span>
                            </div>
                        )}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-text-primary mb-3">Technologies:</h4>
                        <div className="flex flex-wrap gap-2">
                          {industry.technologies.map((tech, idx) =>
                        <span
                          key={idx}
                          className="px-3 py-1 bg-surface text-text-secondary text-xs font-medium rounded-full border border-border">

                              {tech}
                            </span>
                        )}
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <Link
                      href="/get-started"
                      className="flex-1 px-6 py-3 bg-primary text-primary-foreground font-semibold text-center rounded-lg hover:bg-primary/90 transition-colors duration-300">

                        Request Consultation
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-16 md:py-24 bg-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Estimate Your Impact
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Calculate potential carbon reduction and credit generation for your industry
            </p>
          </div>

          <div className="bg-card rounded-2xl p-8 border border-border">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="ArrowTrendingDownIcon" size={32} className="text-primary" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">25-45%</h3>
                <p className="text-sm text-text-secondary">Average Emission Reduction</p>
              </div>

              <div>
                <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="CurrencyDollarIcon" size={32} className="text-accent" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">₹2-5 Cr</h3>
                <p className="text-sm text-text-secondary">Potential Credit Revenue</p>
              </div>

              <div>
                <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="ClockIcon" size={32} className="text-secondary" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">12-18 mo</h3>
                <p className="text-sm text-text-secondary">Implementation Timeline</p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                href="/get-started"
                className="inline-block px-8 py-3 bg-conversion text-conversion-foreground font-heading font-semibold rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300">

                Get Custom Analysis
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-6">
            Transform Your Industry's Sustainability Journey
          </h2>
          <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
            Partner with experts who understand your sector's unique challenges and opportunities
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/get-started"
              className="px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold text-lg rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300">

              Schedule Consultation
            </Link>
            <a
              href={`mailto:dr.nupur@gencarbon.in`}
              className="px-8 py-4 bg-card text-text-primary font-heading font-semibold text-lg rounded-lg border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300">

              Email Us
            </a>
          </div>
        </div>
      </section>
    </main>);

};

export default IndustryESGInteractive;